import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import {
  Save,
  Upload,
  Building,
  MapPin,
  FileText,
  Phone,
  Mail,
  Crown,
  ToggleLeft,
  ToggleRight,
  CalendarClock,
  Settings as SettingsIcon,
} from "lucide-react";

import {
  getCompanyById,
  updateCompanyDetails,
} from "../../services/firestoreService";

// Import da Nova Aba
import ScheduleSettings from "./ScheduleSettings";

const SettingsPage = () => {
  const { companyInfo } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  // Controle de Abas
  const [activeTab, setActiveTab] = useState("general");

  const [formData, setFormData] = useState({
    companyName: "",
    cnpj: "",
    phone: "",
    email: "",
    address: "",
    receiptFooter: "Obrigado pela preferência!",
    logoUrl: "",
    // --- FIDELIDADE ---
    loyaltyEnabled: false,
    loyaltyGoal: 10,
    loyaltyReward: "Corte Grátis",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      if (companyInfo?.id) {
        try {
          const data = await getCompanyById(companyInfo.id);

          if (data) {
            const currentEmail =
              data.email ||
              (data.contact && data.contact.email) ||
              data.contactEmail ||
              "";
            const currentPhone =
              data.phone || (data.contact && data.contact.phone) || "";

            const loyalty = data.settings?.loyalty || {};

            setFormData({
              companyName: data.name || "",
              cnpj: data.cnpj || "",
              phone: currentPhone,
              email: currentEmail,
              address: data.address || "",
              receiptFooter: data.receiptFooter || "Obrigado pela preferência!",
              logoUrl: data.logoUrl || "",
              loyaltyEnabled: loyalty.enabled || false,
              loyaltyGoal: loyalty.goal || 10,
              loyaltyReward: loyalty.reward || "Corte Grátis",
            });
            if (data.logoUrl) {
              setLogoPreview(data.logoUrl);
            }
          }
        } catch (error) {
          console.error("Erro ao carregar configurações:", error);
          toast.error("Erro ao carregar dados da empresa.");
        }
      }
    };
    fetchSettings();
  }, [companyInfo]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleLoyalty = () => {
    setFormData((prev) => ({ ...prev, loyaltyEnabled: !prev.loyaltyEnabled }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        toast.warning("Para esta versão, use uma logo menor que 500KB.");
        return;
      }
      try {
        const base64 = await convertToBase64(file);
        setLogoPreview(base64);
        setFormData((prev) => ({ ...prev, logoUrl: base64 }));
      } catch (err) {
        toast.error("Erro ao processar imagem.");
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyInfo?.id) return;

    setIsLoading(true);
    try {
      const currentName = formData.companyName || "";
      const currentEmail = formData.email || "";
      const currentPhone = formData.phone || "";

      const rawData = {
        name: currentName,
        cnpj: formData.cnpj || "",
        phone: currentPhone,
        email: currentEmail,
        address: formData.address || "",
        receiptFooter: formData.receiptFooter || "",
        logoUrl: formData.logoUrl || null,
        settingsCompleted: true,
        contactName: currentName,
        contactEmail: currentEmail,
        contactPhone: currentPhone,
        contact: {
          name: currentName,
          phone: currentPhone,
          email: currentEmail,
        },
        settings: {
          loyalty: {
            enabled: formData.loyaltyEnabled,
            goal: Number(formData.loyaltyGoal) || 10,
            reward: formData.loyaltyReward || "Corte Grátis",
          },
        },
      };

      const cleanData = JSON.parse(JSON.stringify(rawData));

      await updateCompanyDetails(companyInfo.id, cleanData);
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      console.error("Erro detalhado ao salvar:", error);
      const msg =
        error.code === "permission-denied"
          ? "Sem permissão. Verifique se você é Admin."
          : error.message;
      toast.error(`Erro ao salvar: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-light-bg dark:bg-dark-bg min-h-full transition-colors duration-200">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-light-text-primary dark:text-white">
              Configurações
            </h1>
            <p className="text-sm text-light-text-secondary dark:text-gray-400 mt-1">
              Gerencie os dados e regras da sua barbearia.
            </p>
          </div>
        </div>

        <div className="flex space-x-1 bg-slate-100 dark:bg-dark-card p-1 rounded-xl mb-8 border border-slate-200 dark:border-white/5 w-fit">
          <button
            onClick={() => setActiveTab("general")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeTab === "general"
                ? "bg-white text-brand-blue shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            <SettingsIcon className="w-4 h-4" />
            Geral & Fidelidade
          </button>
          <button
            onClick={() => setActiveTab("schedule")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeTab === "schedule"
                ? "bg-white text-brand-blue shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            <CalendarClock className="w-4 h-4" />
            Agenda & Horários
          </button>
        </div>

        {activeTab === "general" && (
          <div className="animate-in fade-in slide-in-from-left-4 duration-300">
            <form onSubmit={handleSave} className="space-y-6">
              <div className="hidden sm:flex justify-end mb-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 bg-brand-blue hover:bg-brand-blue/90 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-md shadow-brand-blue/20 dark:shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    "Salvando..."
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" /> Salvar Alterações
                    </>
                  )}
                </button>
              </div>

              {/* Seção 1: Identidade Visual */}
              <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6">
                <h2 className="text-lg font-semibold text-light-text-primary dark:text-white mb-4 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-brand-blue dark:text-blue-400" />{" "}
                  Logotipo
                </h2>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="relative w-32 h-32 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-dark-bg overflow-hidden group">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-400 text-center px-2">
                        Sem Logo
                      </span>
                    )}
                    <label
                      htmlFor="logo-upload"
                      className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <Upload className="w-8 h-8 text-white" />
                    </label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <p className="text-sm font-medium text-light-text-primary dark:text-white">
                      Faça upload da sua marca
                    </p>
                    <p className="text-xs text-light-text-secondary dark:text-gray-400 mt-1">
                      Recomendado: PNG ou JPG quadrado. Máx 500KB.
                      <br />
                      Essa imagem sairá no topo dos cupons PDF.
                    </p>
                    <label
                      htmlFor="logo-upload"
                      className="mt-3 inline-flex items-center px-3 py-1.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded text-sm text-light-text-primary dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                    >
                      Escolher Arquivo
                    </label>
                  </div>
                </div>
              </div>

              {/* Seção 2: Dados Gerais */}
              <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6">
                <h2 className="text-lg font-semibold text-light-text-primary dark:text-white mb-4 flex items-center">
                  <Building className="w-5 h-5 mr-2 text-brand-blue dark:text-blue-400" />{" "}
                  Dados da Empresa
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                      Nome da Barbearia
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                      CNPJ
                    </label>
                    <input
                      type="text"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                      Telefone / WhatsApp
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-10 px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                      Email de Contato
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção 3: Endereço */}
              <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6">
                <h2 className="text-lg font-semibold text-light-text-primary dark:text-white mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-brand-blue dark:text-blue-400" />{" "}
                  Endereço
                </h2>
                <div>
                  <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                    Endereço Completo
                  </label>
                  <textarea
                    name="address"
                    rows="3"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors resize-none"
                  ></textarea>
                </div>
              </div>

              {/* Seção 4: Cupom Fiscal */}
              <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6">
                <h2 className="text-lg font-semibold text-light-text-primary dark:text-white mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-brand-blue dark:text-blue-400" />{" "}
                  Cupom Fiscal (PDF)
                </h2>
                <div>
                  <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                    Mensagem de Rodapé
                  </label>
                  <input
                    type="text"
                    name="receiptFooter"
                    value={formData.receiptFooter}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Seção 5: Fidelidade */}
              <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-light-text-primary dark:text-white flex items-center">
                    <Crown className="w-5 h-5 mr-2 text-yellow-500" />{" "}
                    Fidelidade (Gamificação)
                  </h2>
                  <button
                    type="button"
                    onClick={toggleLoyalty}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      formData.loyaltyEnabled
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ring-1 ring-green-500/50"
                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 ring-1 ring-slate-300 dark:ring-slate-600"
                    }`}
                  >
                    {formData.loyaltyEnabled ? (
                      <>
                        <ToggleRight className="w-5 h-5" /> ATIVADO
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5" /> DESATIVADO
                      </>
                    )}
                  </button>
                </div>
                <div
                  className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity duration-300 ${
                    formData.loyaltyEnabled
                      ? "opacity-100"
                      : "opacity-50 pointer-events-none grayscale"
                  }`}
                >
                  <div>
                    <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                      Meta de Cortes
                    </label>
                    <input
                      type="number"
                      name="loyaltyGoal"
                      value={formData.loyaltyGoal}
                      onChange={handleChange}
                      disabled={!formData.loyaltyEnabled}
                      className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Quantos atendimentos para ganhar o prêmio.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-text-secondary dark:text-gray-300 mb-1">
                      Prêmio
                    </label>
                    <input
                      type="text"
                      name="loyaltyReward"
                      value={formData.loyaltyReward}
                      onChange={handleChange}
                      disabled={!formData.loyaltyEnabled}
                      className="w-full px-3 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ex: Corte Grátis, 50% OFF, Pomada...
                    </p>
                  </div>
                </div>
              </div>

              <div className="sm:hidden">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white rounded-lg transition-all duration-200 bg-brand-blue hover:bg-brand-blue/90 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-md shadow-brand-blue/20 dark:shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  Salvar Tudo
                </button>
              </div>
            </form>
          </div>
        )}

        {/* --- CONTEÚDO DA ABA AGENDAMENTO (PASSANDO PROPS) --- */}
        {activeTab === "schedule" && (
          <ScheduleSettings companyId={companyInfo?.id} />
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
